import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { _createMap3DObject, _getMap3dObject, _deleteMap3DObject, _createMap3DObjectProperty, _getMap3DObjectProperties, _deleteMap3DObjectProperty, _updateMap3DObjectProperty, _getMap3DObjectProperty, } from '../shared/httpservice/apis/mapObject';
const createInitialState = () => ({
    loading: false,
    response: null,
    hasError: false,
    error: {},
});
// -----------------------------
// Initial State
// -----------------------------
const initialState = {
    createMapObject: createInitialState(),
    getMapObjects: createInitialState(),
    deleteMapObject: createInitialState(),
    createMapObjectProperty: createInitialState(),
    getMapObjectProperties: createInitialState(),
    getMapObjectProperty: createInitialState(),
    updateMapObjectProperty: createInitialState(),
    deleteMapObjectProperty: createInitialState(),
};
// -----------------------------
// Async Thunks
// -----------------------------
export const createMap3DObjectAction = createAsyncThunk('mapObject3d/create', async ({ siteId, name, mapObjectFile }, { rejectWithValue }) => {
    try {
        const response = await _createMap3DObject(siteId, name, mapObjectFile);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const getMap3DObjectsAction = createAsyncThunk('mapObject3d/getAll', async (siteId, { rejectWithValue }) => {
    try {
        const response = await _getMap3dObject(siteId);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const deleteMap3DObjectAction = createAsyncThunk('mapObject3d/delete', async ({ siteId, mapObjectId }, { rejectWithValue }) => {
    try {
        const response = await _deleteMap3DObject(siteId, mapObjectId);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const createMap3DObjectPropertyAction = createAsyncThunk('mapObject3d/property/create', async (data, { rejectWithValue }) => {
    try {
        const response = await _createMap3DObjectProperty(data);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const getMap3DObjectPropertiesAction = createAsyncThunk('mapObject3d/property/getAll', async (siteId, { rejectWithValue }) => {
    const pageSize = 100;
    try {
        const firstPageResponse = await _getMap3DObjectProperties(siteId, 0, pageSize);
        const { totalPages, content } = firstPageResponse?.data;
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => _getMap3DObjectProperties(siteId, i + 1, pageSize));
        const remainingPagesResponses = await Promise.all(remainingPages);
        const allData = [...content, ...remainingPagesResponses?.flatMap((response) => response?.data?.content)];
        return allData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const getMap3DObjectPropertyAction = createAsyncThunk('mapObject3d/property/getOne', async ({ siteId, mapObjectPropertyId }, { rejectWithValue }) => {
    try {
        const response = await _getMap3DObjectProperty(siteId, mapObjectPropertyId);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const updateMap3DObjectPropertyAction = createAsyncThunk('mapObject3d/property/update', async ({ siteId, mapObjectPropertyId, data, }, { rejectWithValue }) => {
    try {
        const response = await _updateMap3DObjectProperty(siteId, mapObjectPropertyId, data);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const deleteMap3DObjectPropertyAction = createAsyncThunk('mapObject3d/property/delete', async ({ siteId, mapObjectPropertyId }, { rejectWithValue }) => {
    try {
        const response = await _deleteMap3DObjectProperty(siteId, mapObjectPropertyId);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// -----------------------------
// Slice
// -----------------------------
const mapObject3dSlice = createSlice({
    name: 'mapObject3d',
    initialState,
    reducers: {
        resetMapObject3dSlice: () => initialState,
    },
    extraReducers: (builder) => {
        // Utility to reduce repetitive boilerplate
        const addCaseSet = (asyncThunk, stateSlice) => {
            builder
                .addCase(asyncThunk.pending, (state) => {
                stateSlice.loading = true;
                stateSlice.response = null;
                stateSlice.hasError = false;
                stateSlice.error = {};
            })
                .addCase(asyncThunk.fulfilled, (state, action) => {
                stateSlice.loading = false;
                stateSlice.response = action.payload;
                stateSlice.hasError = false;
            })
                .addCase(asyncThunk.rejected, (state, action) => {
                stateSlice.loading = false;
                stateSlice.hasError = true;
                stateSlice.error = action.payload;
            });
        };
        // Manually defining to keep types safe
        builder
            .addCase(createMap3DObjectAction.pending, (state) => {
            state.createMapObject.loading = true;
            state.createMapObject.response = null;
            state.createMapObject.hasError = false;
            state.createMapObject.error = {};
        })
            .addCase(createMap3DObjectAction.fulfilled, (state, action) => {
            state.createMapObject.loading = false;
            state.createMapObject.response = action.payload;
            state.createMapObject.hasError = false;
        })
            .addCase(createMap3DObjectAction.rejected, (state, action) => {
            state.createMapObject.loading = false;
            state.createMapObject.hasError = true;
            state.createMapObject.error = action.payload;
        })
            .addCase(getMap3DObjectsAction.pending, (state) => {
            state.getMapObjects.loading = true;
            state.getMapObjects.response = null;
            state.getMapObjects.hasError = false;
            state.getMapObjects.error = {};
        })
            .addCase(getMap3DObjectsAction.fulfilled, (state, action) => {
            state.getMapObjects.loading = false;
            state.getMapObjects.response = action.payload;
            state.getMapObjects.hasError = false;
        })
            .addCase(getMap3DObjectsAction.rejected, (state, action) => {
            state.getMapObjects.loading = false;
            state.getMapObjects.hasError = true;
            state.getMapObjects.error = action.payload;
        })
            .addCase(deleteMap3DObjectAction.pending, (state) => {
            state.deleteMapObject.loading = true;
            state.deleteMapObject.response = null;
            state.deleteMapObject.hasError = false;
            state.deleteMapObject.error = {};
        })
            .addCase(deleteMap3DObjectAction.fulfilled, (state, action) => {
            state.deleteMapObject.loading = false;
            state.deleteMapObject.response = action.payload;
            state.deleteMapObject.hasError = false;
        })
            .addCase(deleteMap3DObjectAction.rejected, (state, action) => {
            state.deleteMapObject.loading = false;
            state.deleteMapObject.hasError = true;
            state.deleteMapObject.error = action.payload;
        })
            // Map Object Properties
            .addCase(createMap3DObjectPropertyAction.fulfilled, (state, action) => {
            state.createMapObjectProperty.response = action.payload;
        })
            .addCase(getMap3DObjectPropertiesAction.fulfilled, (state, action) => {
            state.getMapObjectProperties.response = { content: action.payload };
        })
            .addCase(getMap3DObjectPropertyAction.fulfilled, (state, action) => {
            state.getMapObjectProperty.response = action.payload;
        })
            .addCase(updateMap3DObjectPropertyAction.fulfilled, (state, action) => {
            state.updateMapObjectProperty.response = action.payload;
        })
            .addCase(deleteMap3DObjectPropertyAction.fulfilled, (state, action) => {
            state.deleteMapObjectProperty.response = action.payload;
        });
    },
});
export const { resetMapObject3dSlice } = mapObject3dSlice.actions;
export default mapObject3dSlice.reducer;
